/**
 * Store Icon Component
 * Custom SVG icon representing a store/branch for menu scope selection
 *
 * @module @experimental-components/IconsComponents/StoreIcon
 */
/**
 * Props for StoreIcon component
 */
interface StoreIconProps {
  /** Icon width */
  width?: number | string;
  /** Icon height */
  height?: number | string;
  /** Icon color - will be applied to fill attribute */
  color?: string;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Store Icon Component
 * Renders a custom SVG icon representing a store/branch
 *
 * @param {StoreIconProps} props - Component properties
 * @returns {React.ReactElement} Store icon component
 *
 * @example
 * <StoreIcon width={88} height={63} color="currentColor" />
 */
const StoreIcon = ({
  width = 88,
  height = 63,
  color = 'currentColor',
  className,
}: StoreIconProps): React.ReactElement => {
  return (
    <svg
      className={className}
      fill="none"
      height={height}
      viewBox="0 0 88 63"
      width={width}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M72.1503 28.6639H16.0412V63H72.1503V28.6639Z" fill={color} />
      <path d="M73.7256 27.0894H17.6165V61.4255H73.7256V27.0894Z" fill="#F8A6B9" />
      <path
        d="M76.33 35.4725C76.33 37.1098 74.3306 37.8655 73.2129 36.669C72.6697 36.0865 72.0479 35.6142 71.0167 35.6142C68.3639 35.6142 68.3639 38.7786 65.7112 38.7786C63.0584 38.7786 63.0584 35.6142 60.4057 35.6142C57.7529 35.6142 57.7529 38.7786 55.1002 38.7786C52.4475 38.7786 52.4475 35.6142 49.7947 35.6142C47.142 35.6142 47.142 38.7786 44.4892 38.7786C41.8365 38.7786 41.8365 35.6142 39.1837 35.6142C36.531 35.6142 36.531 38.7786 33.8783 38.7786C31.2255 38.7786 31.2255 35.6142 28.5728 35.6142C25.92 35.6142 25.92 38.7786 23.2673 38.7786C20.6145 38.7786 20.6145 35.6142 17.9618 35.6142C17.0487 35.6142 16.1356 35.9842 15.3327 36.4801C14.1598 37.1964 12.6484 36.3148 12.6484 34.9373V29.9309L25.7941 14.9198H64.2077L76.33 29.9309V35.4725Z"
        fill={color}
        stroke={color}
        strokeMiterlimit="10"
      />
      <path
        d="M77.919 34.6855C77.919 36.3228 75.9196 37.0785 74.8018 35.882C74.2587 35.2995 73.6368 34.8272 72.6056 34.8272C69.9529 34.8272 69.9529 37.9916 67.3001 37.9916C64.6474 37.9916 64.6474 34.8272 61.9947 34.8272C59.3419 34.8272 59.3419 37.9916 56.6892 37.9916C54.0364 37.9916 54.0364 34.8272 51.3837 34.8272C48.7309 34.8272 48.7309 37.9916 46.0782 37.9916C43.4255 37.9916 43.4255 34.8272 40.7727 34.8272C38.12 34.8272 38.12 37.9916 35.4672 37.9916C32.8145 37.9916 32.8145 34.8272 30.1617 34.8272C27.509 34.8272 27.509 37.9916 24.8563 37.9916C22.2035 37.9916 22.2035 34.8272 19.5508 34.8272C18.6377 34.8272 17.7245 35.1972 16.9216 35.6931C15.7488 36.4094 14.2374 35.5278 14.2374 34.1502V29.1439L26.8635 14.9198H65.2771L77.919 29.1439V34.6855Z"
        fill="#F04E4A"
        stroke={color}
        strokeMiterlimit="10"
      />
      <path d="M14.2221 29.1439H77.9194" stroke={color} strokeMiterlimit="10" />
      <path d="M55.9438 42.8641H35.0004V61.4241H55.9438V42.8641Z" fill={color} />
      <path d="M55.9417 42.8641H35.4754V60.9689H55.9417V42.8641Z" fill="white" />
      <path d="M45.71 42.8641V60.9689" stroke={color} strokeMiterlimit="10" />
      <path
        d="M88 5.87973C88 5.87973 80.6855 6.74969 79.6532 -0.00012207C79.6532 -0.00012207 80.0016 6.64931 72 9.09494C72 9.09494 79.0832 8.09113 81.1968 15.9999C81.1968 15.9999 79.0246 6.68885 88 5.87973Z"
        fill="#F8A6B9"
      />
      <path
        d="M-9.53674e-07 20.7749C-9.53674e-07 20.7749 6.40016 20.0137 7.30348 25.9198C7.30348 25.9198 6.99857 20.1015 14 17.9616C14 17.9616 7.80216 18.84 5.95278 11.9198C5.95278 11.9198 7.85345 20.0669 -9.53674e-07 20.7749Z"
        fill="#F8A6B9"
      />
    </svg>
  );
};

export default StoreIcon;
